import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/brand-logo';
import { LanguageToggle } from '@/components/navigation/language-toggle';
import { buttonVariants, Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';

const navigation = [
  { key: 'cvBuilder', to: routes.cvFeature },
  { key: 'linkedin', to: routes.linkedinFeature },
  { key: 'interview', to: routes.interviewFeature },
  { key: 'pricing', to: routes.pricing },
] as const;

export function PublicHeader() {
  const { t } = useTranslation('common');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  return (
    <header className="public-header">
      <div className="container public-header__inner">
        <BrandLogo />
        <nav className="public-nav" aria-label={t('navigation.primary')}>
          {navigation.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) =>
                cn('public-nav__link focus-ring', isActive && 'is-active')
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="public-header__actions">
          <LanguageToggle />
          <Link className="header-signin focus-ring" to={routes.login}>
            {t('nav.signIn')}
          </Link>
          <Link className={buttonVariants({ size: 'sm' })} to={routes.register}>
            {t('nav.getStarted')}
          </Link>
        </div>
        <Button
          className="mobile-menu-button"
          variant="ghost"
          size="icon"
          aria-expanded={menuOpen}
          aria-controls="mobile-public-menu"
          aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={() => setMenuOpen((value) => !value)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </Button>
      </div>
      {menuOpen && (
        <div id="mobile-public-menu" className="mobile-menu">
          <nav className="container mobile-menu__inner" aria-label={t('navigation.mobile')}>
            {navigation.map((item) => (
              <NavLink key={item.key} to={item.to} onClick={() => setMenuOpen(false)}>
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <div className="mobile-menu__footer">
              <LanguageToggle />
              <Link to={routes.login} onClick={() => setMenuOpen(false)}>
                {t('nav.signIn')}
              </Link>
            </div>
            <Link
              className={buttonVariants({ size: 'lg' })}
              to={routes.register}
              onClick={() => setMenuOpen(false)}
            >
              {t('nav.getStarted')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
